import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import {
  listLegacyWorkspaceDrafts,
  purgeOtherWorkspaceOwners,
  resolveLegacyWorkspaceDrafts,
  setWorkspaceOwner,
} from '@/features/shiftbook/offline/entry-workspace-store';
import {
  acceptSessionBoundary,
  blockSessionRequests,
  expireSession,
  getPreviousSessionOwner,
  getSessionEpoch,
  isSessionExpired,
  ObsoleteSessionError,
  ownerSite,
  sameSessionUser,
  sessionOwner,
  subscribeSessionBoundary,
} from './session-boundary';

type LegacyDraftMetadata = Awaited<ReturnType<typeof listLegacyWorkspaceDrafts>>[number];

import type {
  AuthMeResponse,
  AuthPersonaPresetId,
  AuthSessionUser,
  Site,
} from '@blms/shared/schemas/shiftbook';
import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { createContext, type PropsWithChildren, useCallback, useContext } from 'react';
import { logger } from '@/lib/logging';
import { ApiError } from '@/lib/api-error';
import { resumePausedMutationsWithReporting } from '@/lib/offline/sync';
import { clearPersistedShiftbookClientState } from '@/lib/query-client';
import {
  ActiveSiteRejectedError,
  fetchAuthSession,
  loginWithCustomSession,
  loginWithPreset,
  loginWithSuperadminCredentials,
  logoutAuthSession,
  setAuthActiveSite,
} from './api/client';
import { getStoredAuthMode, setStoredAuthMode } from './auth-mode-storage';
import { clearExplicitLogout, markExplicitLogout } from './logout-intent';
import { clearPendingAuthFlow, hasPendingAuthFlow } from './pending-auth-flow';

const AUTH_SESSION_QUERY_KEY = ['auth-session'] as const;
const DEFAULT_AUTH_MODE: AuthMeResponse['authMode'] = 'dev';

function buildUnauthenticatedSession(
  authMode: AuthMeResponse['authMode'] = getStoredAuthMode() ?? DEFAULT_AUTH_MODE,
): AuthMeResponse {
  return { authenticated: false, authMode };
}

function toUnauthenticatedSession(authMode: AuthMeResponse['authMode']): AuthMeResponse {
  return {
    authenticated: false,
    authMode,
  };
}

function isShiftbookQuery(queryKey: QueryKey): boolean {
  const key = queryKey[0];
  return typeof key === 'string' && key.startsWith('shiftbook-');
}

async function removeShiftbookData(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({
    predicate: (query) => isShiftbookQuery(query.queryKey),
  });

  queryClient.removeQueries({
    predicate: (query) => isShiftbookQuery(query.queryKey),
  });
}

async function clearOfflineMutationState(queryClient: QueryClient): Promise<void> {
  queryClient.getMutationCache().clear();
}

async function clearPersistedStateBestEffort(): Promise<boolean> {
  try {
    await clearPersistedShiftbookClientState();
    return true;
  } catch (error) {
    logger.warn('Persisted auth cleanup failed; retrying on the next session acceptance.', {
      event: 'auth.cleanup.persistence_failed',
      error,
    });
    return false;
  }
}

async function clearShiftbookClientState(queryClient: QueryClient): Promise<boolean> {
  await removeShiftbookData(queryClient);
  await clearOfflineMutationState(queryClient);
  const persistedCleanupComplete = await clearPersistedStateBestEffort();
  if (typeof caches !== 'undefined') {
    try {
      await Promise.all([
        caches.delete('api-cache'),
        caches.delete('shiftbook-attachment-thumbnails'),
      ]);
    } catch {
      // Some private browsing modes expose CacheStorage but reject its operations.
    }
  }
  return persistedCleanupComplete;
}

export interface AuthContextValue {
  session: AuthMeResponse;
  user: AuthSessionUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isBusy: boolean;
  loginWithPreset: (presetId: AuthPersonaPresetId) => Promise<AuthMeResponse>;
  loginAsSuperadmin: (username: string, password: string) => Promise<AuthMeResponse>;
  recoverCustomSession?: () => Promise<AuthMeResponse>;
  logout: () => Promise<void>;
  setActiveSite: (site: Site) => Promise<AuthMeResponse>;
  refreshSession: () => Promise<AuthMeResponse>;
  sessionExpired?: boolean;
  resetToList?: boolean;
  acknowledgeContextReset?: () => void;
  legacyDrafts?: LegacyDraftMetadata[];
  resolveLegacyDrafts?: (action: 'restore' | 'discard') => Promise<void>;
}

const SAFE_FALLBACK_AUTH_CONTEXT: AuthContextValue = {
  session: buildUnauthenticatedSession(),
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isBusy: false,
  loginWithPreset: async () => {
    throw new Error('AuthProvider is missing from the component tree.');
  },
  loginAsSuperadmin: async () => {
    throw new Error('AuthProvider is missing from the component tree.');
  },
  logout: async () => {
    throw new Error('AuthProvider is missing from the component tree.');
  },
  setActiveSite: async () => {
    throw new Error('AuthProvider is missing from the component tree.');
  },
  refreshSession: async () => buildUnauthenticatedSession(),
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const lastSession = useRef<AuthMeResponse | null>(null);
  const cleanupPending = useRef(false);
  const acceptanceQueue = useRef<Promise<unknown> | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [resetToList, setResetToList] = useState(false);
  const [legacyDrafts, setLegacyDrafts] = useState<LegacyDraftMetadata[]>([]);
  const sessionExpired = useSyncExternalStore(subscribeSessionBoundary, isSessionExpired);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === 'blms-verified-workspace-owner' &&
        !sameSessionUser(
          event.newValue,
          sessionOwner(lastSession.current ?? buildUnauthenticatedSession()),
        )
      )
        expireSession();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const acceptSession = useCallback(
    (
      incoming: AuthMeResponse,
      explicitLogin = false,
      siteSwitch = false,
      expectedEpoch?: number,
    ) => {
      const accept = async () => {
        let nextSession = incoming;
        let acceptanceEpoch = expectedEpoch ?? getSessionEpoch();
        const assertCurrentEpoch = () => {
          if (acceptanceEpoch !== getSessionEpoch()) throw new ObsoleteSessionError();
        };
        assertCurrentEpoch();
        const current = lastSession.current;
        if (!nextSession.authenticated) {
          if (current?.authenticated) {
            expireSession();
            return current;
          }
          return nextSession;
        }
        const recovering = isSessionExpired();
        const previousOwner = current ? sessionOwner(current) : getPreviousSessionOwner();
        const previousSite = ownerSite(previousOwner);
        if (
          previousSite &&
          sameSessionUser(previousOwner, sessionOwner(nextSession)) &&
          (recovering || hasPendingAuthFlow()) &&
          !siteSwitch &&
          nextSession.user.siteRoles[previousSite] &&
          previousSite !== nextSession.user.activeSite
        ) {
          let restored: AuthMeResponse | undefined;
          try {
            restored = await setAuthActiveSite(previousSite, { recovery: true });
          } catch (error) {
            if (error instanceof ApiError && !(error instanceof ActiveSiteRejectedError)) {
              expireSession();
              throw error;
            }
            if (!(error instanceof ActiveSiteRejectedError)) {
              logger.warn('Previous site restore failed; using the login active site.', {
                event: 'auth.recovery.site_restore_failed',
                error,
              });
            }
          }
          assertCurrentEpoch();
          if (restored) {
            if (
              !restored.authenticated ||
              !sameSessionUser(previousOwner, sessionOwner(restored)) ||
              restored.user.activeSite !== previousSite
            ) {
              expireSession();
              throw new Error('Session recovery could not restore the active site.');
            }
            nextSession = restored;
          }
        }
        const nextOwner = sessionOwner(nextSession)!;
        const changedOwner = previousOwner !== null && !sameSessionUser(previousOwner, nextOwner);
        const needsCleanup =
          changedOwner || (previousOwner === null && (explicitLogin || hasPendingAuthFlow()));
        if (changedOwner) {
          setTransitioning(true);
          blockSessionRequests();
          acceptanceEpoch = getSessionEpoch();
          setWorkspaceOwner(null);
        }
        try {
          if (needsCleanup) {
            cleanupPending.current = true;
            cleanupPending.current = !(await clearShiftbookClientState(queryClient));
          } else {
            if (cleanupPending.current)
              cleanupPending.current = !(await clearPersistedStateBestEffort());
            if (previousOwner !== null && previousOwner !== nextOwner) {
              // Site changes refresh reads but keep this user's drafts and queued saves.
              await removeShiftbookData(queryClient);
            }
          }
          assertCurrentEpoch();
          await purgeOtherWorkspaceOwners(nextOwner);
          assertCurrentEpoch();
          setWorkspaceOwner(nextOwner);
          const legacy = await listLegacyWorkspaceDrafts();
          assertCurrentEpoch();
          setLegacyDrafts(legacy);
          // No unowned/foreign persisted writes may become visible or execute.
          for (const mutation of queryClient.getMutationCache().getAll()) {
            const variables = mutation.state.variables as { owner?: string } | undefined;
            if (!sameSessionUser(variables?.owner, nextOwner))
              queryClient.getMutationCache().remove(mutation);
          }
          if (
            changedOwner ||
            (!siteSwitch && previousSite !== nextSession.user.activeSite && previousSite !== null)
          )
            setResetToList(true);
          lastSession.current = nextSession;
          setStoredAuthMode(nextSession.authMode);
          if (explicitLogin || hasPendingAuthFlow()) clearPendingAuthFlow();
          clearExplicitLogout();
          acceptSessionBoundary(nextOwner);
          if ((!current || recovering || explicitLogin) && legacy.length === 0)
            void resumePausedMutationsWithReporting(queryClient);
          return nextSession;
        } catch (error) {
          // A successful auth/site request may already have changed the server.
          // Keep retries possible, but never reopen the previous client context.
          setWorkspaceOwner(null);
          if (needsCleanup) {
            cleanupPending.current = true;
            lastSession.current = null;
            setLegacyDrafts([]);
            queryClient.setQueryData(
              AUTH_SESSION_QUERY_KEY,
              toUnauthenticatedSession(nextSession.authMode),
            );
          }
          expireSession();
          logger.warn('Session acceptance failed; authentication must be revalidated.', {
            event: 'auth.acceptance.failed',
            error,
          });
          throw error;
        } finally {
          setTransitioning(false);
        }
      };
      const result = (acceptanceQueue.current ?? Promise.resolve()).then(accept);
      acceptanceQueue.current = result.catch(() => undefined);
      return result;
    },
    [queryClient],
  );

  const sessionQuery = useQuery<AuthMeResponse>({
    queryKey: AUTH_SESSION_QUERY_KEY,
    queryFn: async () => {
      for (;;) {
        const epoch = getSessionEpoch();
        const next = await fetchAuthSession();
        try {
          return await acceptSession(next, false, false, epoch);
        } catch (error) {
          if (!(error instanceof ObsoleteSessionError)) throw error;
          // The shared cookie may have changed while startup or cleanup waited.
          // Refetch inside this query; never publish the obsolete identity.
        }
      }
    },
    retry: false,
    refetchOnMount: 'always',
    staleTime: 30_000,
  });

  const finishLogin = async (next: AuthMeResponse) => {
    const accepted = await acceptSession(next, true);
    queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, accepted);
  };
  const loginMutation = useMutation({
    mutationFn: (presetId: AuthPersonaPresetId) => loginWithPreset(presetId),
    retry: false,
    onSuccess: finishLogin,
  });
  const superadminLoginMutation = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      loginWithSuperadminCredentials(username, password),
    retry: false,
    onSuccess: finishLogin,
  });
  const customLoginMutation = useMutation({
    mutationFn: async () => {
      const current = lastSession.current;
      if (!current?.authenticated || current.authMode !== 'dev')
        throw new Error('No custom dev session to recover.');
      return loginWithCustomSession(current.user);
    },
    retry: false,
    onSuccess: finishLogin,
  });
  const logoutMutation = useMutation({
    mutationFn: logoutAuthSession,
    retry: false,
    onSuccess: async () => {
      const authMode = lastSession.current?.authMode ?? getStoredAuthMode() ?? DEFAULT_AUTH_MODE;
      setTransitioning(true);
      blockSessionRequests();
      setWorkspaceOwner(null);
      cleanupPending.current = true;
      try {
        const persistedCleanupComplete = await clearShiftbookClientState(queryClient);
        await purgeOtherWorkspaceOwners(null);
        cleanupPending.current = !persistedCleanupComplete;
      } catch (error) {
        logger.warn('Logout cleanup failed; retrying on the next session acceptance.', {
          event: 'auth.logout.cleanup_failed',
          error,
        });
      } finally {
        lastSession.current = null;
        setLegacyDrafts([]);
        acceptSessionBoundary(null);
        clearPendingAuthFlow();
        markExplicitLogout();
        queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, toUnauthenticatedSession(authMode));
        setTransitioning(false);
      }
    },
  });
  const setActiveSiteMutation = useMutation({
    mutationFn: (site: Site) => setAuthActiveSite(site),
    retry: false,
    onSuccess: async (next) => {
      const accepted = await acceptSession(next, false, true);
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, accepted);
    },
  });
  const session = sessionQuery.data ?? buildUnauthenticatedSession();
  const user = session.authenticated ? session.user : null;
  const isLoading =
    sessionQuery.isPending ||
    (sessionQuery.isFetching && !sessionQuery.isFetchedAfterMount) ||
    transitioning;

  const value: AuthContextValue = {
    session,
    user,
    isAuthenticated: Boolean(user) && !sessionExpired,
    isLoading,
    sessionExpired,
    resetToList,
    legacyDrafts,
    acknowledgeContextReset: () => setResetToList(false),
    resolveLegacyDrafts: async (action) => {
      const owner = sessionOwner(session);
      if (!owner || sessionExpired) return;
      const epoch = getSessionEpoch();
      await resolveLegacyWorkspaceDrafts(action, owner);
      if (epoch !== getSessionEpoch()) return;
      setLegacyDrafts([]);
      void resumePausedMutationsWithReporting(queryClient);
    },
    isBusy:
      customLoginMutation.isPending ||
      loginMutation.isPending ||
      superadminLoginMutation.isPending ||
      logoutMutation.isPending ||
      setActiveSiteMutation.isPending,
    recoverCustomSession: () => customLoginMutation.mutateAsync(),
    loginWithPreset: (presetId) => loginMutation.mutateAsync(presetId),
    loginAsSuperadmin: (username, password) =>
      superadminLoginMutation.mutateAsync({ username, password }),
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    setActiveSite: (site) => setActiveSiteMutation.mutateAsync(site),
    refreshSession: async () => {
      const result = await sessionQuery.refetch();
      return result.data ?? buildUnauthenticatedSession();
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext) ?? SAFE_FALLBACK_AUTH_CONTEXT;
}

export const authSessionQueryKey = AUTH_SESSION_QUERY_KEY;
